import { useEffect, useState } from "react"
import * as Dialog from "@radix-ui/react-dialog"
import { motion, useReducedMotion } from "motion/react"
import {
  AlertCircle,
  ArrowsRight,
  Camera01,
  Image01,
  Check,
  Plus,
  RefreshCw01,
  Trash01,
  XClose,
} from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { FlowCta, FlowHeading, PhasePane, QuietLink, StickyActions } from "../flow-chrome"
import { CAPTURE_COPY, shotTitle, VERDICT_CHIP } from "./capture-copy"
import { missingRequired } from "./shot-plan"
import { effectiveVerdict } from "./shot-result"
import type { CaptureState, PlannedShot, ShotId, ShotRecord } from "./types"

// "Your photos" — every slot of the walk in order, always fixable from here:
// tap a photo to see it big and retake or remove it, tap an empty slot to take
// it. Retake/Remove are always visible (no hover-only controls on a phone).

const C = CAPTURE_COPY.review

type TileState =
  | { kind: "photo"; record: ShotRecord }
  | { kind: "skipped" }
  | { kind: "empty" }

function tileState(capture: CaptureState, shot: PlannedShot): TileState {
  const record = capture.shots[shot.id]
  if (record) return { kind: "photo", record }
  if (capture.skipped.includes(shot.id)) return { kind: "skipped" }
  return { kind: "empty" }
}

function needsLook(r: ShotRecord): boolean {
  const v = effectiveVerdict(r)
  return !!v && v !== "good" && !r.accepted
}

export function ContactSheet({
  plan,
  capture,
  onTake,
  onRetry,
  onRemove,
  onAddSpot,
  onGallery,
  onMove,
  onSend,
  sending,
  pending,
  notice,
}: {
  plan: readonly PlannedShot[]
  capture: CaptureState
  // Open the camera on these shots, in this order.
  onTake: (ids: ShotId[]) => void
  onRetry: (key: string) => void
  onRemove: (key: string) => void
  onAddSpot: () => void
  onGallery: () => void
  // Re-file a photo under another (empty) spot, or back to extras.
  onMove: (key: string, to: PlannedShot | null) => void
  onSend: () => void
  sending: boolean
  // Photos still uploading/queued.
  pending: number
  notice: string | null
}) {
  const [viewing, setViewing] = useState<{ key: string; title: string; shotId: ShotId | null } | null>(null)
  const missing = missingRequired(plan, capture)
  const photoCount =
    plan.filter((s) => capture.shots[s.id]).length + capture.extras.length

  const viewingRecord = viewing
    ? capture.shots[viewing.key as ShotId] ?? capture.extras.find((r) => r.key === viewing.key)
    : undefined

  return (
    <PhasePane className="pt-2">
      <FlowHeading className="text-[28px] font-bold leading-[1.15] tracking-tight text-ink">
        {C.title}
      </FlowHeading>
      <p className="mt-2 text-[15px] leading-relaxed text-zinc-600">{C.sub}</p>

      {notice && (
        <p role="status" className="mt-4 flex gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-[14px] font-medium leading-snug text-amber-900">
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          {notice}
        </p>
      )}

      <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-4" aria-label={C.title}>
        {plan.map((shot) => (
          <li key={shot.id}>
            <Tile
              shot={shot}
              state={tileState(capture, shot)}
              onOpen={(record) => setViewing({ key: record.key, title: shotTitle(shot), shotId: shot.id })}
              onTake={() => onTake([shot.id])}
            />
          </li>
        ))}
      </ul>

      {capture.extras.length > 0 && (
        <>
          <h2 className="mt-7 text-[15px] font-semibold text-ink">{C.extras}</h2>
          <ul className="mt-3 grid grid-cols-3 gap-2">
            {capture.extras.map((r) => (
              <li key={r.key}>
                <button
                  type="button"
                  onClick={() => setViewing({ key: r.key, title: C.extras, shotId: null })}
                  className="relative block aspect-square w-full overflow-hidden rounded-2xl bg-zinc-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                  aria-label={`${C.extras}: open photo`}
                >
                  <img src={r.previewUrl} alt="" className="size-full object-cover" />
                  <StatusDot record={r} />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <button
        type="button"
        onClick={onAddSpot}
        className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-[15px] font-semibold text-ink transition-colors duration-150 hover:bg-ink/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
      >
        <Plus className="size-5" aria-hidden />
        {C.addSpot}
      </button>
      <QuietLink onClick={onGallery}>
        <Image01 className="size-4" aria-hidden />
        {C.addFromGallery}
      </QuietLink>

      <StickyActions className="space-y-1">
        {missing.length > 0 ? (
          <>
            <FlowCta onClick={() => onTake(missing.map((s) => s.id))}>
              <Camera01 className="size-5" aria-hidden />
              {C.takeMissing(missing.length)}
            </FlowCta>
            <QuietLink onClick={onSend} disabled={sending}>
              {photoCount === 0 ? C.sendNone : C.send}
            </QuietLink>
          </>
        ) : (
          <FlowCta onClick={onSend} loading={sending}>
            {sending && pending > 0 ? C.sendBusy(pending) : C.send}
          </FlowCta>
        )}
      </StickyActions>

      <PhotoViewer
        open={!!viewing && !!viewingRecord}
        title={viewing?.title ?? ""}
        record={viewingRecord}
        onClose={() => setViewing(null)}
        onRetake={
          viewing?.shotId
            ? () => {
                const id = viewing.shotId as ShotId
                setViewing(null)
                onTake([id])
              }
            : null
        }
        onRemove={() => {
          if (viewing) onRemove(viewing.key)
          setViewing(null)
        }}
        moveTargets={
          viewing
            ? plan.filter((s) => s.id !== viewing.key && !capture.shots[s.id])
            : []
        }
        isExtra={!!viewing && !viewing.shotId}
        onMove={(to) => {
          if (viewing) onMove(viewing.key, to)
          setViewing(null)
        }}
        onRetry={
          viewing && viewingRecord?.status === "failed"
            ? () => {
                onRetry(viewing.key)
                setViewing(null)
              }
            : null
        }
      />
    </PhasePane>
  )
}

function Tile({
  shot,
  state,
  onOpen,
  onTake,
}: {
  shot: PlannedShot
  state: TileState
  onOpen: (record: ShotRecord) => void
  onTake: () => void
}) {
  const title = shotTitle(shot)
  if (state.kind !== "photo") {
    return (
      <button
        type="button"
        onClick={onTake}
        className="group block w-full text-left focus-visible:outline-none"
      >
        <span className="grid aspect-[4/3] w-full place-items-center rounded-2xl border-2 border-dashed border-zinc-300 bg-white text-zinc-500 transition-colors duration-150 group-hover:border-zinc-400 group-focus-visible:border-ink">
          <Camera01 className="size-6" aria-hidden />
        </span>
        <span className="mt-1.5 block text-[14px] font-semibold leading-snug text-ink">{title}</span>
        <span className="block text-[13px] text-zinc-500">
          {state.kind === "skipped" ? C.skipped : C.missing}
          {shot.optional && " · optional"}
        </span>
      </button>
    )
  }

  const { record } = state
  return (
    <button
      type="button"
      onClick={() => onOpen(record)}
      className="group block w-full text-left focus-visible:outline-none"
    >
      <span className="relative block aspect-[4/3] w-full overflow-hidden rounded-2xl bg-zinc-200 ring-offset-2 ring-offset-paper group-focus-visible:ring-2 group-focus-visible:ring-ink">
        <img src={record.previewUrl} alt="" className="size-full object-cover" />
        <StatusDot record={record} />
      </span>
      <span className="mt-1.5 block text-[14px] font-semibold leading-snug text-ink">{title}</span>
      <StatusLine record={record} />
    </button>
  )
}

function StatusDot({ record }: { record: ShotRecord }) {
  if (record.status === "uploading" || record.status === "queued") {
    return (
      <span className="absolute right-2 top-2 grid size-7 place-items-center rounded-full bg-black/55 backdrop-blur-sm">
        <span className="size-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden />
      </span>
    )
  }
  if (record.status === "failed" || needsLook(record)) {
    return <span className="absolute right-2 top-2 size-3.5 rounded-full bg-amber-500 ring-2 ring-white" aria-hidden />
  }
  if (record.verdict) {
    return (
      <span className="absolute right-2 top-2 grid size-7 place-items-center rounded-full bg-signal text-white">
        <Check className="size-4" strokeWidth={3} aria-hidden />
      </span>
    )
  }
  return null
}

function StatusLine({ record }: { record: ShotRecord }) {
  let text: string
  let tone = "text-zinc-500"
  if (record.status === "uploading") text = C.uploading
  else if (record.status === "queued") text = C.queued
  else if (record.status === "failed") {
    text = C.failed
    tone = "text-amber-800"
  } else if (needsLook(record)) {
    text = `${C.retakeSuggested} · ${VERDICT_CHIP[effectiveVerdict(record)!] ?? ""}`
    tone = "text-amber-800"
  } else text = C.checked
  return <span className={cx("block text-[13px]", tone)}>{text}</span>
}

function PhotoViewer({
  open,
  title,
  record,
  onClose,
  onRetake,
  onRemove,
  onRetry,
  moveTargets,
  isExtra,
  onMove,
}: {
  open: boolean
  title: string
  record: ShotRecord | undefined
  onClose: () => void
  onRetake: (() => void) | null
  onRemove: () => void
  // The photo didn't send: try sending it again.
  onRetry: (() => void) | null
  // Empty spots this photo could be moved to.
  moveTargets: readonly PlannedShot[]
  isExtra: boolean
  onMove: (to: PlannedShot | null) => void
}) {
  const reduce = useReducedMotion()
  const [moving, setMoving] = useState(false)
  useEffect(() => {
    if (!open) setMoving(false)
  }, [open])
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black" />
        <Dialog.Content
          className="fixed inset-0 z-50 flex flex-col bg-black text-white outline-none"
          aria-describedby={undefined}
        >
          <div className="flex h-14 items-center justify-between px-2 pt-[env(safe-area-inset-top)]">
            <Dialog.Title className="truncate pl-3 text-[16px] font-semibold text-white">{title}</Dialog.Title>
            <Dialog.Close
              className="grid size-11 place-items-center rounded-full text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white"
              aria-label={C.close}
            >
              <XClose className="size-6" aria-hidden />
            </Dialog.Close>
          </div>
          <div className="relative min-h-0 flex-1">
            {record && (
              <motion.img
                src={record.previewUrl}
                alt={title}
                initial={reduce ? false : { opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                className="absolute inset-0 size-full object-contain"
              />
            )}
          </div>
          {record && (record.status === "failed" || needsLook(record)) && (
            <p className="flex items-center justify-center gap-2 px-4 pt-3 text-[14px] font-medium text-amber-300">
              <AlertCircle className="size-4" aria-hidden />
              {record.status === "failed" ? C.failed : VERDICT_CHIP[effectiveVerdict(record)!]}
            </p>
          )}
          {onRetry && (
            <div className="px-4 pt-3">
              <button
                type="button"
                onClick={onRetry}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-white/10 text-[15px] font-semibold text-white hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-white"
              >
                <RefreshCw01 className="size-5" aria-hidden />
                {CAPTURE_COPY.coach.retry}
              </button>
            </div>
          )}
          {moving && (
            <div className="max-h-[40svh] overflow-y-auto px-4 pt-3" role="group" aria-label={C.moveTitle}>
              <p className="pb-2 text-[14px] font-semibold text-white/70">{C.moveTitle}</p>
              <ul className="space-y-1.5">
                {moveTargets.map((target) => (
                  <li key={target.id}>
                    <button
                      type="button"
                      onClick={() => onMove(target)}
                      className="flex h-12 w-full items-center rounded-xl bg-white/10 px-4 text-left text-[15px] font-semibold text-white hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-white"
                    >
                      {shotTitle(target)}
                    </button>
                  </li>
                ))}
                {!isExtra && (
                  <li>
                    <button
                      type="button"
                      onClick={() => onMove(null)}
                      className="flex h-12 w-full items-center rounded-xl bg-white/10 px-4 text-left text-[15px] font-semibold text-white hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-white"
                    >
                      {C.moveToExtras}
                    </button>
                  </li>
                )}
              </ul>
            </div>
          )}
          <div className="flex gap-2 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">
            {(moveTargets.length > 0 || !isExtra) && (
              <button
                type="button"
                onClick={() => setMoving((m) => !m)}
                aria-expanded={moving}
                className="flex h-14 flex-1 items-center justify-center gap-2 rounded-2xl border border-white/25 text-[15px] font-semibold text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white"
              >
                <ArrowsRight className="size-5" aria-hidden />
                {C.move}
              </button>
            )}
            <button
              type="button"
              onClick={onRemove}
              className="flex h-14 flex-1 items-center justify-center gap-2 rounded-2xl border border-white/25 text-[15px] font-semibold text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white"
            >
              <Trash01 className="size-5" aria-hidden />
              {C.remove}
            </button>
            {onRetake && (
              <button
                type="button"
                onClick={onRetake}
                className="flex h-14 flex-1 items-center justify-center gap-2 rounded-2xl bg-white text-[15px] font-semibold text-ink hover:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                <RefreshCw01 className="size-5" aria-hidden />
                {C.retake}
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
